import { AgentImpersonationExchangeResponse, ApiClient, PropallyAgentV1Api } from "../api/generated/src";

const ACCESS_TOKEN_KEY = "access_token";
const ADMIN_LOGIN_INFO_KEY = "admin_login_info";
const ONE_TIME_CODE_KEY = "one_time_code";
const IMPERSONATION_LOGIN_FAILED_KEY = "impersonation_login_failed";

export type AdminLoginInfo = {
  id: number | null;
  name: string | null;
};

const getStoredAccessToken = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.sessionStorage.getItem(ACCESS_TOKEN_KEY);
};

const setStoredAccessToken = (accessToken: string) => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  syncApiClientAuthState();
};

const syncApiClientAuthState = () => {
  const apiClient = ApiClient.instance;
  const accessToken = getStoredAccessToken();

  if (accessToken) {
    apiClient.defaultHeaders = {
      ...apiClient.defaultHeaders,
      Authorization: `Bearer ${accessToken}`,
    };
  } else {
    const { Authorization: _removed, ...rest } = apiClient.defaultHeaders;
    apiClient.defaultHeaders = rest;
  }

  // access_tokenログイン時はCookieセッションを混在させない
  apiClient.enableCookies = !accessToken;
};

// sessionStorage復元値は任意の形を取りうるため、unknown から安全に正規化する
const toAdminLoginInfo = (raw: unknown): AdminLoginInfo | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const value = raw as Record<string, unknown>;
  const parsedId = Number(value.id);
  const id = Number.isFinite(parsedId) ? parsedId : null;
  const name = typeof value.name === "string" ? value.name : null;

  if (!id && !name) {
    return null;
  }

  return { id, name };
};

const setAdminLoginInfo = (adminLoginInfo: AdminLoginInfo | null) => {
  if (typeof window === "undefined") {
    return;
  }

  if (!adminLoginInfo) {
    window.sessionStorage.removeItem(ADMIN_LOGIN_INFO_KEY);
    return;
  }

  window.sessionStorage.setItem(ADMIN_LOGIN_INFO_KEY, JSON.stringify(adminLoginInfo));
};

const removeImpersonationQueryFromUrl = () => {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete(ONE_TIME_CODE_KEY);
  window.history.replaceState(
    window.history.state,
    document.title,
    `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
  );
};

const exchangeOneTimeCode = async (
  oneTimeCode: string
): Promise<{
  accessToken: string;
  adminLoginInfo: AdminLoginInfo | null;
}> => {
  return new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1ImpersonationExchangePost(
      { one_time_code: oneTimeCode },
      (error: object, data: AgentImpersonationExchangeResponse) => {
        if (error || !data?.access_token) {
          reject(new Error("Failed to exchange impersonation code."));
          return;
        }

        // Nav表示用に、exchangeレスポンスから sessionStorage 保存向けの最小情報を組み立てる
        const adminLoginInfo = toAdminLoginInfo({
          id: data.impersonation?.admin_user_id,
          name: data.impersonation?.admin_user_name,
        });
        resolve({ accessToken: data.access_token, adminLoginInfo });
      }
    );
  });
};

const setImpersonationLoginFailed = () => {
  if (typeof window === "undefined") {
    return;
  }
  window.sessionStorage.setItem(IMPERSONATION_LOGIN_FAILED_KEY, "1");
};

export const consumeImpersonationLoginFailed = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  const hasFailed = window.sessionStorage.getItem(IMPERSONATION_LOGIN_FAILED_KEY) === "1";
  if (hasFailed) {
    window.sessionStorage.removeItem(IMPERSONATION_LOGIN_FAILED_KEY);
  }
  return hasFailed;
};

export const clearTokenBasedLoginContext = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  window.sessionStorage.removeItem(ADMIN_LOGIN_INFO_KEY);
  window.sessionStorage.removeItem(IMPERSONATION_LOGIN_FAILED_KEY);
  syncApiClientAuthState();
};

export const getAdminLoginInfo = (): AdminLoginInfo | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const storedValue = window.sessionStorage.getItem(ADMIN_LOGIN_INFO_KEY);
  if (!storedValue) {
    return null;
  }

  try {
    return toAdminLoginInfo(JSON.parse(storedValue));
  } catch (_error) {
    return null;
  }
};

export const initializeAccessToken = async () => {
  if (typeof window === "undefined") {
    return;
  }

  syncApiClientAuthState();

  const currentUrl = new URL(window.location.href);
  const oneTimeCode = currentUrl.searchParams.get(ONE_TIME_CODE_KEY);
  if (!oneTimeCode) {
    return;
  }

  // one-time codeで新規ログインするため、既存トークン文脈はクリア
  clearTokenBasedLoginContext();
  try {
    const { accessToken, adminLoginInfo } = await exchangeOneTimeCode(oneTimeCode);
    setStoredAccessToken(accessToken);
    setAdminLoginInfo(adminLoginInfo);
  } catch (_error) {
    // 再利用済みcode等で失敗した場合は、tokenなしの状態にする
    clearTokenBasedLoginContext();
    setImpersonationLoginFailed();
  } finally {
    removeImpersonationQueryFromUrl();
    syncApiClientAuthState();
  }
};
